import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, Transition } from 'framer-motion';

interface ScrollAnimationProps {
  children: React.ReactNode;
  animation?: 'fadeInUp' | 'fadeInLeft' | 'fadeInRight' | 'scaleIn' | 'subtleFadeIn' | 'slideInUp' | 'slideInLeft' | 'slideInRight' | 'zoomIn' | 'staggerChildren' | 'parallaxUp' | 'parallaxDown';
  delay?: number;
  duration?: number;
  className?: string;
  stagger?: number;
  distance?: number;
}

const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  animation = 'fadeInUp',
  delay = 0,
  duration = 0.6,
  className = '',
  stagger = 0.1,
  distance = 50
}) => {
  const ref = useRef(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  const isInView = useInView(ref, { 
    once: true, 
    margin: '-50px 0px -50px 0px',
    amount: 0.2
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const animations = {
    fadeInUp: {
      initial: { opacity: 0, y: distance },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    fadeInLeft: {
      initial: { opacity: 0, x: -distance },
      animate: { opacity: 1, x: 0 },
      transition: { 
        duration, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    fadeInRight: {
      initial: { opacity: 0, x: distance },
      animate: { opacity: 1, x: 0 },
      transition: { 
        duration, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    scaleIn: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { 
        duration, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    subtleFadeIn: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: duration * 0.8, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    slideInUp: {
      initial: { opacity: 0, y: distance * 1.5 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: duration * 1.2, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    slideInLeft: {
      initial: { opacity: 0, x: -distance * 1.5 },
      animate: { opacity: 1, x: 0 },
      transition: { 
        duration: duration * 1.2, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    slideInRight: {
      initial: { opacity: 0, x: distance * 1.5 },
      animate: { opacity: 1, x: 0 },
      transition: { 
        duration: duration * 1.2, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    zoomIn: {
      initial: { opacity: 0, scale: 0.5 },
      animate: { opacity: 1, scale: 1 },
      transition: { 
        duration: duration * 1.1, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    staggerChildren: {
      initial: { opacity: 0 },
      animate: { 
        opacity: 1,
        transition: {
          staggerChildren: stagger,
          delayChildren: delay
        }
      },
      transition: { 
        duration, 
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    parallaxUp: {
      initial: { opacity: 0, y: distance * 2 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: duration * 1.5, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    },
    parallaxDown: {
      initial: { opacity: 0, y: -distance * 2 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: duration * 1.5, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      } as Transition
    }
  };

  const animationConfig = animations[animation];

  if (prefersReducedMotion) {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3, delay }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={animationConfig.initial}
      animate={isInView ? animationConfig.animate : animationConfig.initial}
      transition={animationConfig.transition}
      className={className}
      style={{ 
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden',
        perspective: 1000
      }}
    >
      {animation === 'staggerChildren' ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{
            staggerChildren: stagger,
            delayChildren: delay
          }}
        >
          {children}
        </motion.div>
      ) : (
        children
      )}
    </motion.div>
  );
};

export default ScrollAnimation;